/**
 * BulkEvalConfirmDialog.jsx — bulk evaluation confirm modal.
 *
 * RC EMPLOYER.OVERAGE.REMOVAL (2026-02-27):
 *   No overage acceptance path. If the selected batch would exceed
 *   the monthly allowance, the dialog refuses to dispatch and shows
 *   an honest "Monthly allowance reached — upgrade or wait until
 *   reset" message with a "View plans" CTA. Operators can still
 *   continue when the batch fits within remaining headroom.
 *
 * WP8 (Rev 4.4 §H.5):
 *   Client-side integrity pre-check. Before /dispatch is invoked
 *   the dialog reads the candidates list (which carries the WP7
 *   `integrity_classification` field authored by the WP6 classifier)
 *   and, if any candidate is `unresolved` / `missing_check` (either
 *   mode) or `excluded` (explicit-ids mode only), renders an inline
 *   blocker with doctrine copy variants. `Continue` is disabled.
 *   The server-side gate at /dispatch remains the authoritative
 *   boundary (§I.2) — this dialog is a courtesy pre-check and
 *   ALSO gracefully surfaces a server-side 422
 *   INTEGRITY_DISPOSITION_REQUIRED envelope if the client pre-check
 *   is stale.
 *
 * Calls:
 *   GET  /api/employer/v1/roles/{id}/candidates            (integrity pre-check read)
 *   POST /api/employer/v1/roles/{id}/evaluate-bulk/preview
 *   POST /api/employer/v1/roles/{id}/evaluate-bulk/dispatch
 *
 * The dispatch route enforces the same cap AND the integrity gate
 * server-side. This client is a courtesy gate, not the security
 * boundary.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Layers, ExternalLink, ShieldAlert } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { apiFetch } from '@/lib/api';
import { setMarker } from '@/lib/employerBulkRunMarker';

function formatPeriodEnd(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return null;
  }
}

function randomIdempotencyKey() {
  return `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function BulkEvalConfirmDialog({
  open, onOpenChange, roleId, candidateIds, onDispatched,
  onReviewFlagged,
}) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(false);

  // WP8 §H.5 pre-check state.
  // `classifications` is a map of candidate_id -> {classification, disposition}
  // sourced from GET /candidates (WP7 attaches WP6 classifier state).
  const [classifications, setClassifications] = useState(null);
  const [classificationsLoading, setClassificationsLoading] = useState(false);
  // If the server-side gate rejects (dispatch-time snapshot may
  // differ from our pre-check due to another operator's action),
  // capture the enumerated block envelope for rendering.
  const [serverBlock, setServerBlock] = useState(null);

  const explicitMode = Array.isArray(candidateIds) && candidateIds.length > 0;

  useEffect(() => {
    if (!open) {
      setPreview(null); setError(null);
      setClassifications(null); setServerBlock(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiFetch(
          `/api/employer/v1/roles/${roleId}/evaluate-bulk/preview`,
          {
            method: 'POST',
            body:   JSON.stringify({ candidate_ids: candidateIds }),
          },
        );
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Preview failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // WP8 §H.5 — pre-check candidate integrity classifications from
    // WP7's list endpoint. This is a courtesy pre-check; the server
    // /dispatch route re-runs the WP6 classifier authoritatively.
    (async () => {
      setClassificationsLoading(true);
      try {
        const data = await apiFetch(
          `/api/employer/v1/roles/${roleId}/candidates`,
        );
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const map = {};
        items.forEach((r) => {
          if (r && r.id) {
            map[r.id] = {
              classification: r.integrity_classification || null,
              disposition:    r.integrity_disposition   || null,
              status:         r.integrity_status        || null,
            };
          }
        });
        setClassifications(map);
      } catch {
        // Do not block on pre-check failure — the server dispatch
        // gate remains authoritative. Silent so we don't surface
        // a scary error for a purely optimistic UX hint.
        if (!cancelled) setClassifications({});
      } finally {
        if (!cancelled) setClassificationsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, roleId, candidateIds]);

  const overBundle  = Number(preview?.over_bundle || 0);
  const wouldExceed = Boolean(preview?.would_exceed_cap) || overBundle > 0;

  // ── WP8 §H.5 integrity pre-check ─────────────────────────────
  //
  // Doctrine (Rev 4.4 §I.2):
  //   explicit_ids: unresolved | excluded | missing_check → BLOCK
  //   all_parsed:   unresolved | missing_check           → BLOCK
  //                 excluded is silently omitted.
  //
  // The dialog does not know the exact all_parsed population, so in
  // all_parsed mode we scan the full role candidate list.
  const preCheck = useMemo(() => {
    if (!classifications) {
      return { ready: false, blocking: false, unresolved: [], excluded: [], missingCheck: [] };
    }
    const ids = explicitMode
      ? candidateIds
      : Object.keys(classifications);
    const unresolved   = [];
    const excluded     = [];
    const missingCheck = [];
    ids.forEach((cid) => {
      const c = classifications[cid];
      if (!c) return;
      if (c.classification === 'unresolved')    unresolved.push(cid);
      else if (c.classification === 'excluded') excluded.push(cid);
      else if (c.classification === 'missing_check') missingCheck.push(cid);
    });
    const blocking = explicitMode
      ? (unresolved.length + excluded.length + missingCheck.length) > 0
      : (unresolved.length + missingCheck.length) > 0;
    return { ready: true, blocking, unresolved, excluded, missingCheck };
  }, [classifications, candidateIds, explicitMode]);

  const canConfirm  = Boolean(preview)
    && !loading
    && !wouldExceed
    && !preCheck.blocking
    && !serverBlock;
  const periodEnd   = formatPeriodEnd(preview?.period_ends_at);

  const onConfirm = async () => {
    setBusy(true);
    setError(null);
    setServerBlock(null);
    try {
      const idem = randomIdempotencyKey();
      const body = await apiFetch(
        `/api/employer/v1/roles/${roleId}/evaluate-bulk/dispatch`,
        {
          method: 'POST',
          headers: { 'Idempotency-Key': idem },
          body: JSON.stringify({ candidate_ids: candidateIds }),
        },
      );
      toast.success(`Bulk evaluation queued · ${body?.count || candidateIds.length} candidate(s)`);
      if (body?.job_id) {
        setMarker(roleId, { jobId: body.job_id, dispatchedAt: Date.now() });
      }
      onDispatched?.(body);
      onOpenChange?.(false);
    } catch (err) {
      const detail = err?.data?.detail || err?.data || {};
      const code = detail.code || err?.code || 'DISPATCH_FAILED';
      const msg  = detail.message || err?.message || 'Dispatch failed.';
      // WP8 §H.5 — surface a server-side 422
      // INTEGRITY_DISPOSITION_REQUIRED envelope with enumerated IDs
      // even when the client pre-check missed it (stale local state).
      if (code === 'INTEGRITY_DISPOSITION_REQUIRED') {
        setServerBlock({
          unresolved:    Array.isArray(detail.unresolved)    ? detail.unresolved    : [],
          excluded:      Array.isArray(detail.excluded)      ? detail.excluded      : [],
          missingCheck:  Array.isArray(detail.missing_check) ? detail.missing_check : [],
          mode:          detail.mode || (explicitMode ? 'explicit_ids' : 'all_parsed'),
        });
        toast.error('Integrity disposition required for one or more dossiers.');
      } else {
        setError(`${code}: ${msg}`);
        toast.error(`${code}: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  };

  // Combined blocker source: server-side 422 wins when present
  // (freshest truth), otherwise the client pre-check.
  const activeBlock = useMemo(() => {
    if (serverBlock) return serverBlock;
    if (preCheck.blocking) {
      return {
        unresolved:   preCheck.unresolved,
        excluded:     preCheck.excluded,
        missingCheck: preCheck.missingCheck,
        mode:         explicitMode ? 'explicit_ids' : 'all_parsed',
      };
    }
    return null;
  }, [serverBlock, preCheck, explicitMode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg bg-ndor-surface border-ndor-edge text-ndor-ink"
        data-testid="bulk-eval-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-ndor-ink flex items-center gap-2">
            <Layers className="h-4 w-4 text-ndor-ink-muted" />
            Run bulk evaluation
          </DialogTitle>
          <DialogDescription className="text-sm text-ndor-ink-muted leading-relaxed">
            Evaluations are drawn from your monthly allowance. Beyond-cap dispatches are blocked.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-6" data-testid="bulk-eval-preview-loading">
            <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
          </div>
        )}

        {!loading && preview && preview.plan_active === false && (
          <div
            className="rounded-lg border border-slate-500/40 bg-slate-500/10 p-4 text-sm text-ndor-ink-muted"
            data-testid="bulk-eval-plan-inactive"
          >
            Plan not activated — contact your administrator to enable evaluation.
          </div>
        )}

        {!loading && preview && preview.plan_active && (
          <div className="space-y-3" data-testid="bulk-eval-preview-cards">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div
                className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4"
                data-testid="bulk-eval-within-card"
              >
                <p className="text-[10px] uppercase tracking-wider text-emerald-300/80">
                  Within allowance
                </p>
                <p className="text-2xl font-semibold mt-0.5 text-emerald-300">
                  {preview.within_bundle || 0}
                </p>
                <p className="text-[10px] text-ndor-ink-muted">
                  drawn from monthly cap
                </p>
              </div>
              <div
                className="rounded-lg border border-ndor-edge bg-ndor-bg/40 p-4"
                data-testid="bulk-eval-used-card"
              >
                <p className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">
                  Used this period
                </p>
                <p className="text-2xl font-semibold mt-0.5 text-ndor-ink">
                  {preview.monthly_used ?? '—'}
                </p>
                <p className="text-[10px] text-ndor-ink-muted">
                  of {preview.monthly_cap ?? '—'} this period
                </p>
              </div>
              <div
                className="rounded-lg border border-ndor-edge bg-ndor-bg/40 p-4"
                data-testid="bulk-eval-reset-card"
              >
                <p className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">
                  Resets
                </p>
                <p className="text-base font-semibold mt-1 text-ndor-ink">
                  {periodEnd || '—'}
                </p>
                <p className="text-[10px] text-ndor-ink-muted">
                  next billing period
                </p>
              </div>
            </div>

            {Number(preview.skip_already_complete || 0) > 0 && (
              <p
                className="text-[10px] text-ndor-ink-muted"
                data-testid="bulk-eval-skip-note"
              >
                {preview.skip_already_complete} candidate(s) already evaluated will be skipped.
              </p>
            )}

            {/* RC EMPLOYER.OVERAGE.REMOVAL — beyond cap is a HARD BLOCK. */}
            {wouldExceed && (
              <div
                className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 space-y-2"
                data-testid="bulk-eval-blocked-banner"
              >
                <p className="text-sm font-medium text-amber-100">
                  Monthly evaluation allowance reached.
                </p>
                <p className="text-xs text-amber-200/90 leading-relaxed">
                  This dispatch would consume {overBundle} evaluation(s) beyond your current monthly allowance.
                  {periodEnd
                    ? <> Your allowance resets on <span className="font-medium">{periodEnd}</span>.</>
                    : null}
                  {' '}Upgrade your workspace or wait until reset.
                </p>
                <Link
                  to="/employer/account?tab=plans"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-100 hover:text-amber-50 underline-offset-4 hover:underline"
                  data-testid="bulk-eval-view-plans-link"
                  onClick={() => onOpenChange?.(false)}
                >
                  View plans <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
        )}

        {/* WP8 §H.5 — integrity pre-check / server-block blocker.
            Doctrine copy varies by dispatch mode:
              - all_parsed: "Evaluate All cannot proceed yet."
              - explicit_ids: "The following selected dossiers cannot be evaluated:"
            Enumerated IDs are surfaced verbatim. `excluded` is
            shown for context in all_parsed mode (informational only,
            not a blocker in that mode). */}
        {classificationsLoading && !classifications && (
          <p
            className="text-[10px] text-ndor-ink-muted"
            data-testid="bulk-eval-integrity-precheck-loading"
          >
            Checking dossier integrity dispositions…
          </p>
        )}
        {activeBlock && (
          <div
            className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-4 space-y-3"
            data-testid="bulk-eval-integrity-blocker"
            data-block-source={serverBlock ? 'server' : 'client'}
            data-block-mode={activeBlock.mode}
          >
            <p className="text-sm font-medium text-rose-100 flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" />
              {activeBlock.mode === 'all_parsed'
                ? 'Evaluate All cannot proceed yet.'
                : 'The following selected dossiers cannot be evaluated:'}
            </p>
            {activeBlock.mode === 'all_parsed' ? (
              <div className="text-xs text-rose-100/90 leading-relaxed space-y-1">
                {activeBlock.unresolved.length > 0 && (
                  <p data-testid="bulk-eval-integrity-unresolved-line">
                    <span className="font-medium">{activeBlock.unresolved.length}</span>
                    {' '}dossier{activeBlock.unresolved.length === 1 ? '' : 's'} require an Include or Exclude decision before evaluation.
                  </p>
                )}
                {activeBlock.missingCheck.length > 0 && (
                  <p data-testid="bulk-eval-integrity-missing-line">
                    <span className="font-medium">{activeBlock.missingCheck.length}</span>
                    {' '}dossier{activeBlock.missingCheck.length === 1 ? '' : 's'} are missing the required integrity check and cannot be evaluated until the check is repaired.
                  </p>
                )}
                {activeBlock.excluded.length > 0 && (
                  <p
                    className="text-rose-200/80"
                    data-testid="bulk-eval-integrity-excluded-context-line"
                  >
                    {activeBlock.excluded.length}
                    {' '}dossier{activeBlock.excluded.length === 1 ? '' : 's'} have already been excluded from evaluation.
                  </p>
                )}
                <p className="pt-1">Resolve the outstanding decisions below.</p>
              </div>
            ) : (
              <div className="text-xs text-rose-100/90 leading-relaxed space-y-1.5">
                {activeBlock.unresolved.length > 0 && (
                  <p data-testid="bulk-eval-integrity-unresolved-line">
                    <span className="font-medium">
                      Requires a decision ({activeBlock.unresolved.length}):
                    </span>{' '}
                    <span className="font-mono text-[11px] break-all"
                          data-testid="bulk-eval-integrity-unresolved-ids">
                      {activeBlock.unresolved.join(', ')}
                    </span>
                  </p>
                )}
                {activeBlock.excluded.length > 0 && (
                  <p data-testid="bulk-eval-integrity-excluded-line">
                    <span className="font-medium">
                      Already excluded ({activeBlock.excluded.length}):
                    </span>{' '}
                    <span className="font-mono text-[11px] break-all"
                          data-testid="bulk-eval-integrity-excluded-ids">
                      {activeBlock.excluded.join(', ')}
                    </span>
                  </p>
                )}
                {activeBlock.missingCheck.length > 0 && (
                  <p data-testid="bulk-eval-integrity-missing-line">
                    <span className="font-medium">
                      Missing integrity check ({activeBlock.missingCheck.length}):
                    </span>{' '}
                    <span className="font-mono text-[11px] break-all"
                          data-testid="bulk-eval-integrity-missing-ids">
                      {activeBlock.missingCheck.join(', ')}
                    </span>
                  </p>
                )}
                <p className="pt-1 text-rose-100/80">
                  Resolve the decisions or remove the affected dossiers from the selection, then try again.
                </p>
              </div>
            )}
            {typeof onReviewFlagged === 'function' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onReviewFlagged();
                  onOpenChange?.(false);
                }}
                className="h-8 text-[11px] border-rose-500/40 text-rose-100 hover:bg-rose-500/10"
                data-testid="bulk-eval-integrity-review-cta"
              >
                Review dossiers awaiting decision
              </Button>
            )}
          </div>
        )}

        {error && (
          <div className="text-xs text-rose-400 flex items-start gap-2" data-testid="bulk-eval-error">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            className="border-ndor-edge text-ndor-ink min-h-9 px-4"
            data-testid="bulk-eval-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!canConfirm || busy}
            className="bg-blue-500 hover:bg-blue-500/90 text-white min-h-9 px-4"
            data-testid="bulk-eval-confirm-btn"
            title={
              activeBlock
                ? 'Integrity disposition required — resolve the flagged dossiers first.'
                : wouldExceed
                  ? `Monthly allowance reached. Upgrade workspace or wait until ${periodEnd || 'reset'}.`
                  : undefined
            }
          >
            {busy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Dispatching…</>
            ) : (
              'Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
